import React from "react";
import { graphql, HeadProps, PageProps } from "gatsby";
import { HomepageNewQuery } from "types/graphql-types";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import { isPresent } from "src/utils/checks";
import type { Organization } from "schema-dts";
import { jsonLdScriptProps } from "react-schemaorg";
import organizationSchema from "../components/SEO/organization-json-ld-data";
import { PageContext } from "types/pageContext";
import { prefetchMetaForPromoApi } from "components/SEO/utils";
import SanityBuilderToComponents from "components/PageBuilder";
import ClientOnly from "components/ClientOnly";
import EmotionalHeroBanner from "components/EmotionalHeroBanner/EmotionalHeroBanner";
import { useNotifyAnalyticsForComponentTestShown } from "src/ab-test-experiments/client/use-experiment-tag";
import { match } from "ts-pattern";

const MaybeLastSeenProducts = React.lazy(
  () => import("components/LastSeenProducts/MaybeLastSeenProducts")
);
const FindYourCruiseSearchBox = React.lazy(
  () => import("components/FindYourCruiseSearchBox/FindYourCruiseSearchBox")
);

type Query = HomepageNewQuery & { page: NonNullable<HomepageNewQuery["page"]> };

const HomepageTemplate: React.FC<React.PropsWithChildren<PageProps<Query>>> = ({
  data
}) => {
  // TODO A/B Test: uncomment this and isExperimentActive condition to enable the A/B test
  // const experiment = useNotifyAnalyticsForComponentTestShown({
  //   experimentId: "homepage-simple",
  //   enabled: true
  // });

  const isExperimentActive = false; //experiment?.segmentId === "b";

  const page = data.page;
  const allSections = page.sections?.filter(isPresent) || [];

  const allSectionsWithExperiment = isExperimentActive
    ? allSections.filter((x) =>
        match(x)
          .with(
            { __typename: "SanityPageMosaic" },
            (section) =>
              section._key != "78c0e50dda79" && section._key != "d1261db5f04b"
          )
          .with(
            { __typename: "SanityFaresMosaic" },
            (section) => section._key != "5946e0df6155"
          )
          .with(
            { __typename: "SanityCollectionSlider" },
            (section) =>
              section._key != "1c938e0d670e" && section._key != "464ce9f4f5cc"
          )
          .with(
            { __typename: "SanityVideoSection" },
            (section) => section._key != "2ed2740aa85c"
          )
          .otherwise(() => true)
      )
    : allSections;

  return (
    <React.Fragment>
      <EmotionalHeroBanner
        data={page.heroMedia}
        buttonsInverted={false}
      ></EmotionalHeroBanner>

      <React.Suspense>
        <FindYourCruiseSearchBox
          fycPageUrl={data.fycPagePath}
          firstLastDepartureCruiseDates={data.firstLastDepartureCruiseDates}
          backgroundColor="white"
          enableSticky
        />
      </React.Suspense>

      <ClientOnly>
        <React.Suspense>
          <MaybeLastSeenProducts />
        </React.Suspense>
      </ClientOnly>

      <React.Fragment>
        {allSectionsWithExperiment.length > 0 && (
          <SanityBuilderToComponents
            sections={allSectionsWithExperiment}
            initialBackgroundColor="white"
          />
        )}
      </React.Fragment>
    </React.Fragment>
  );
};

export const Head = (props: HeadProps<HomepageNewQuery, PageContext>) => {
  const { data, pageContext } = props;

  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={data.page?.metadata?.openGraph}
      excludeFromSitemap={false}
      jsonLd={[
        jsonLdScriptProps<Organization>({
          "@context": "https://schema.org",
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          ...organizationSchema
        })
      ]}
    >
      {prefetchMetaForPromoApi()}
    </GatsbyHeadOpenGraph>
  );
};

export const query = graphql`
  query HomepageNew($id: String!) {
    firstLastDepartureCruiseDates: firstLastDepartureCruiseDates {
      minYearMonth
      maxYearMonth
    }

    page: sanityHomepage(id: { eq: $id }) {
      metadata {
        openGraph {
          ...OpenGraphMetaTags
        }
      }

      heroMedia {
        ...HeroMedia
      }

      enableUrgentMessageSlide
      urgentMessageSlide {
        localized
      }

      sections {
        ...PageBuilderSections
        ... on SanitySharedSectionRef {
          ...PageBuilderSharedSectionRef
        }
      }
    }

    fycPagePath
  }
`;

export default HomepageTemplate;
